import {
  Component,
  Input,
  ViewChild,
  ElementRef,
  AfterViewInit,
  ViewChildren,
  QueryList,
  Renderer2,
  OnInit,
  SimpleChanges,
  OnChanges,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Colors } from '../../../shared/enums/shared.enums';
import { Caasnewsticker } from '../../../shared/interfaces/caas/caas.interface';
import { newstickerItem } from '../../../shared/interfaces/distractor.interface';
import { LinkComponent } from '../../atoms/link/link.component';
import { HrefType } from '../../../shared/enums/href-type.enums';
import LinkType from '../../../shared/enums/link-type.enums';
import { CapitalizePipe } from '../../capitalize.pipe';
import { CaasRoutesService } from '../../../services/caas-routes.service';
import { Router } from '@angular/router';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { safeAssign } from '../../../shared/utils/safe-assign.util';
import { NewstickerLoaderComponent } from './newsticker-loader/newsticker-loader.component';

@Component({
  selector: 'hdi-newsticker',
  templateUrl: './newsticker.component.html',
  styleUrls: ['./newsticker.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    LinkComponent,
    CapitalizePipe,
    NewstickerLoaderComponent,
  ],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class NewstickerComponent implements AfterViewInit, OnInit, OnChanges {
  @Input() caasData?: Caasnewsticker;
  @Input() items: newstickerItem[] = [];
  @Input() bgColor: Colors = Colors.WHITE;

  @ViewChildren('newstickerItems') newstickerItems: QueryList<ElementRef> =
    new QueryList<ElementRef>();
  @ViewChild('newstickerContainer') newstickerContainer: ElementRef =
    {} as ElementRef;

  inline: LinkType = LinkType.INLINE;

  constructor(
    public readonly el: ElementRef,
    private readonly renderer: Renderer2,
    private readonly caasRoutesService: CaasRoutesService,
    private readonly router: Router,
    private readonly errorHandler: ErrorHandlerService,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {}
  isWithinTimeframe: boolean = false;
  public isLoading = true;

  ngOnInit(): void {
    this.updateDynamicGradient();
    this.checkTimeframe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['bgColor']) {
      this.updateDynamicGradient();
    }
    if (changes['caasData']) {
      this.setCaasData(changes['caasData'].currentValue);
      if (isPlatformBrowser(this.platformId)) {
        this.isLoading = false;
      }
    }
  }
  setCaasData(caasData: Caasnewsticker): void {
    let linkText = '';
    let header = '';
    let linkUrl = '';
    let linkFragment: string | undefined = undefined;
    let hrefType: HrefType = HrefType.ANGULAR_ROUTE;
    let linkTitle = '';
    let linkRel = '';
    let linkTarget: '_self' | '_blank' = '_self';

    safeAssign(
      () => {
        linkText = caasData.formData?.lt_linktext?.value || '';
      },
      'Assigning link text',
      this.errorHandler
    );

    safeAssign(
      () => {
        header = caasData.formData?.st_header?.value || '';
      },
      'Assigning header',
      this.errorHandler
    );

    safeAssign(
      () => {
        linkUrl =
          caasData.formData?.st_link?.value?.formData?.lt_link_external
            ?.value ||
          (this.caasRoutesService.getSeoRouteByIdentifier(
            caasData.formData?.st_link?.value?.formData?.lt_link_internal?.value
              ?.identifier ?? '',
            this.router.config
          )?.path ??
            '');
      },
      'Assigning link URL',
      this.errorHandler
    );

    safeAssign(
      () => {
        linkFragment =
          caasData.formData?.st_link?.value?.formData?.st_anchor_name?.value?.trim();
      },
      'Assigning link fragment',
      this.errorHandler
    );
    safeAssign(
      () => {
        hrefType =
          caasData.formData?.st_link?.value?.formData?.lt_link_type?.value
            ?.identifier === 'external'
            ? HrefType.EXTERNAL
            : HrefType.ANGULAR_ROUTE;
      },
      'Assigning hrefType',
      this.errorHandler
    );
    safeAssign(
      () => {
        linkTitle =
          caasData.formData?.st_link?.value?.formData?.lt_link_title?.value ||
          '';
      },
      'Assigning link title',
      this.errorHandler
    );
    safeAssign(
      () => {
        linkRel =
          caasData.formData?.st_link?.value?.formData?.lt_link_follow?.value
            ?.identifier ?? '';
      },
      'Assigning link rel',
      this.errorHandler
    );
    safeAssign(
      () => {
        linkTarget =
          caasData.formData?.st_link?.value?.formData?.lt_link_type?.value
            ?.identifier === 'external'
            ? '_blank'
            : '_self';
      },
      'Assigning link target',
      this.errorHandler
    );

    this.items = [
      {
        link: {
          url: linkUrl,
          text: linkText,
          title: linkTitle,
          rel: linkRel,
          target: linkTarget,
          type: hrefType,
          fragment: linkFragment,
        },
        content: header,
      },
    ];
  }
  checkTimeframe(): void {
    safeAssign(
      () => {
        const startDate = this.caasData?.formData?.st_startdate?.value;
        const endDate = this.caasData?.formData?.st_enddate?.value;
        const now = new Date();
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;
        this.isWithinTimeframe =
          (!start || now >= start) && (!end || now <= end);
      },
      'Checking timeframe',
      this.errorHandler
    );
  }

  ngAfterViewInit(): void {
    this.cloneItems();
  }
  private clonedItems: boolean = false;

  cloneItems(): void {
    if (!this.newstickerItems || this.newstickerItems.length === 0) {
      console.warn('No newsticker items to clone.');
      return;
    }
    if (this.clonedItems) {
      return;
    } // Perform cloning logic
    this.clonedItems = true;
    const containerWidth: number =
      this.newstickerContainer.nativeElement.offsetWidth;
    const itemsWidth: number = this.calculateItemsWidth();

    const clonesNeeded: number = Math.ceil(containerWidth / itemsWidth) * 2;

    for (let i = 0; i < clonesNeeded; i++) {
      this.newstickerItems.forEach((itemRef) => {
        const item: HTMLElement = itemRef.nativeElement;
        const clone: Node = item.cloneNode(true);
        const parentElement =
          this.newstickerItems.first.nativeElement.parentElement;
        if (parentElement) {
          parentElement.appendChild(clone);
        }
      });
    }
  }

  calculateItemsWidth(): number {
    if (!this.newstickerItems || this.newstickerItems.length === 0) {
      return 0;
    }

    return this.newstickerItems.reduce(
      (totalWidth, item) => totalWidth + (item.nativeElement.offsetWidth || 0),
      0
    );
  }

  updateDynamicGradient(): void {
    // Default the color to white if it is invalid
    const isValidHex = /^#([0-9A-F]{3}){1,2}$/i.test(this.bgColor);
    if (!isValidHex || this.bgColor.toUpperCase() === '#FFFFFF') {
      this.bgColor = Colors.WHITE;
    }

    const styleContent = `
      .newsticker-container:before {
        background: linear-gradient(90deg, ${this.bgColor} 5%, transparent 55%, transparent 100%);
      }
      .newsticker-container:after {
        background: linear-gradient(270deg, ${this.bgColor} 5%, transparent 55%, transparent 100%);
      }
    `;

    // Remove any existing style tag to avoid duplication
    const existingStyleTag = this.el.nativeElement.querySelector(
      'style.dynamic-gradient'
    );
    if (existingStyleTag) {
      this.renderer.removeChild(this.el.nativeElement, existingStyleTag);
    }

    // Create a new style tag with the updated gradient
    const styleTag = this.renderer.createElement('style');
    this.renderer.setProperty(styleTag, 'type', 'text/css');
    this.renderer.addClass(styleTag, 'dynamic-gradient'); // Add a class for identification
    this.renderer.setProperty(styleTag, 'innerHTML', styleContent);

    // Append the style tag to the component's root element
    this.renderer.appendChild(this.el.nativeElement, styleTag);
  }
}
